import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { HANDLE_ERROR_AND_THROW } from '@helios/utils'
import { Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { aggregateInfoOf } from '../grid/row.adapter'
import { RowOrigin } from '../models/source-key.model'
import { WorkbenchRow } from '../models/workbench-row.model'

/**
 * helios-flex-transactions' matched-counterparts read (helios PVAL-26450): given one Enfusion
 * transaction's flex primary key, it answers the Prometheus Custody and Trading transactions the
 * server has matched it to.
 *
 * With `<base href="/helios/">` this resolves to
 * `/helios/helios-flex-transactions/transactions/matches`, which nginx rewrites to the service's
 * `/api/transactions/matches` — the same host, and so the same LOCAL-ONLY `proxy.conf.beta.json`
 * entry, as the trade read and the dialog-metadata endpoints.
 */
export const TRANSACTION_MATCHES_URL = `helios-flex-transactions/transactions/matches`

/**
 * The request body: the edited row's flex primary key.
 *
 * `transactionId` is the grid row's `rowId` (`ROW_ID_FIELD.api`) and `transactionType` is the grid
 * row's `fields.transactionType` AS RECEIVED from the read API — never the Enfusion trade document's
 * `transactionType`, which is a different vocabulary.
 */
export interface TransactionMatchesRequest {
  transactionId: string
  transactionType: string
}

/** One matched Prometheus transaction. `aggInfo` is an object, a JSON string, or null. */
export interface TransactionMatchElement {
  extTranId: string
  aggInfo: unknown
}

/** The response body. Either list may be missing or null, which reads as empty. */
export interface TransactionMatchesResponse {
  prometheusCustody?: TransactionMatchElement[] | null
  prometheusTrading?: TransactionMatchElement[] | null
}

/**
 * What a load answered: the matched counterparts already mapped to {@link WorkbenchRow}s, and the
 * failure if there was one.
 *
 * **The error is RETURNED, not thrown**, as in `HttpTradeReadService`: the Edit dialog is already
 * open under its matches loader when this answers, so a failure must still settle the read (lowering
 * the loader, with no fetched counterparts) — and must not vanish either, so the caller can tell the
 * user the comparison columns are empty because the read failed, not because nothing matched.
 */
export interface TransactionMatchesResult {
  custody: WorkbenchRow[]
  trading: WorkbenchRow[]
  error: unknown | null
}

const listOf = (value: unknown): unknown[] =>
  Array.isArray(value) ? value : []

const idOf = (element: unknown): string | undefined => {
  if (element == null || typeof element !== 'object') return undefined
  const raw = (element as Record<string, unknown>)['extTranId']
  if (raw == null) return undefined
  const id = String(raw).trim()
  return id === '' ? undefined : id
}

const rowsOf = (
  elements: unknown,
  origin: RowOrigin,
  accountId: string,
): WorkbenchRow[] =>
  listOf(elements).flatMap(element => {
    const rowId = idOf(element)
    // Without an id there is nothing to show it as, and nothing to de-duplicate it against.
    if (rowId == null) return []
    const row: WorkbenchRow = {
      rowId,
      // The origin comes from the list the element sits in, never from `extTranSource`: the server
      // has already said which side it is.
      origin,
      group: undefined,
      ignored: false,
      breaks: { high: 0, medium: 0 },
      accountId,
      aggregateInfo: aggregateInfoOf(element as Record<string, unknown>),
      // `prometheusValueOf` falls back to `fields[key]` when `aggregateInfo` lacks one, so this must
      // exist even though the matches response carries nothing but `aggInfo`.
      fields: {},
    }
    return [row]
  })

/**
 * Maps a matches response onto `cust` / `trad` counterpart rows. Pure, so the mapping is testable
 * without HTTP. Anything that is not the documented shape reads as no matches.
 */
export function counterpartRowsFrom(
  response: unknown,
  accountId?: string,
): { custody: WorkbenchRow[]; trading: WorkbenchRow[] } {
  const body =
    response != null && typeof response === 'object'
      ? (response as Record<string, unknown>)
      : {}
  const account = accountId ?? ''
  return {
    custody: rowsOf(body['prometheusCustody'], 'cust', account),
    trading: rowsOf(body['prometheusTrading'], 'trad', account),
  }
}

/**
 * Reads the server-matched Prometheus counterparts of one Enfusion transaction, for the Create/Edit
 * Transaction dialog's comparison columns in edit mode.
 */
@Injectable()
export class HttpTransactionMatchesService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Emits exactly once and completes. Never errors: a failed call answers empty lists with the
   * failure in `error`.
   */
  load(
    request: TransactionMatchesRequest,
    accountId?: string,
  ): Observable<TransactionMatchesResult> {
    const body: TransactionMatchesRequest = {
      transactionId: request.transactionId,
      transactionType: request.transactionType,
    }

    return this.http
      .post<unknown>(TRANSACTION_MATCHES_URL, body, {
        // Without this the app's global `ErrorInterceptor` returns EMPTY on every HTTP error: the
        // observable COMPLETES WITHOUT EMITTING, `catchError` never runs, no `error` ever reaches the
        // caller, and the open Edit dialog cannot tell the user its comparison is missing — it just
        // shows empty Custody/Trading columns as if nothing matched. The interceptor still reports
        // the error centrally; this only asks it to rethrow afterwards so the caller can react too.
        context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
      })
      .pipe(
        map(response => ({
          ...counterpartRowsFrom(response, accountId),
          error: null,
        })),
        catchError((error: unknown) => {
          // 404 = no such transaction for that key; 400 = a malformed key; else a helios 500.
          console.error(
            `[transactions-2] matches read failed for transaction ${body.transactionId} ` +
              `(${body.transactionType}); the Edit dialog shows no server counterparts`,
            error,
          )
          return of({ custody: [], trading: [], error: detailOf(error) })
        }),
      )
  }
}

/**
 * The part of an HTTP failure worth showing — the upstream body where there is one, else a plain
 * synthesised summary (an `HttpErrorResponse` itself serialises to `{}`). Never null, so a failure
 * is always distinguishable from success.
 */
const detailOf = (error: unknown): unknown => {
  const response = error as {
    status?: number
    statusText?: string
    url?: string
    message?: string
    error?: unknown
  } | null
  const body = response?.error
  if (body != null && body !== '') return body
  return {
    status: response?.status ?? null,
    statusText: response?.statusText ?? null,
    url: response?.url ?? TRANSACTION_MATCHES_URL,
    message: response?.message ?? String(error),
  }
}
